import { Box, Button, SimpleGrid, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from 'components/Input/Input';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { useProfileData } from './hooks/useProfileData';
import { useProfileDataMutation } from './hooks/useProfileDataMutation';
import { INVALID_EMAIL_REGEX } from 'utils/constants';
import { usePopup } from 'context/PopupContext/PopupContext';
import { useEventTracking } from 'hooks/useEventTracking';

const schema = yup.object({
  name: yup
    .string()
    .transform((value) => value?.trim())
    .required('checkout.fieldIsRequired')
    .min(2, 'profile.name.minLengthError'),
  email: yup
    .string()
    .nullable()
    .transform((value) => {
      const trimmedValue = value?.trim();
      return trimmedValue || null;
    })
    .test('email-validation', 'profile.email.invalidEmailError', (value) => {
      if (!value) {
        return true;
      }
      return INVALID_EMAIL_REGEX.test(value);
    })
    .default(null),
});
export type FormValues = yup.InferType<typeof schema>;

export const PersonalInfo = () => {
  const { eventTracking } = useEventTracking();
  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'profile_page',
      url: window.location.href,
    });
  }, [eventTracking]);
  const { data, isLoading } = useProfileData();
  const { mutate } = useProfileDataMutation();
  const { showPopup } = usePopup();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors, isDirty },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    data &&
      reset({
        name: data.name,
        email: data?.email,
      });
  }, [data, reset]);

  const onSubmit = handleSubmit((formData: FormValues) => {
    eventTracking.profile.updated({
      updatedFields: [formData.name, formData.email as string],
    });
    try {
      mutate(
        {
          name: formData.name,
          email: formData.email,
        },
        {
          onSuccess: () => {
            showPopup({
              message: 'Details updated successfully',
              type: 'SUCCESS',
            });
          },
        },
      );
    } catch (error) {
      showPopup({
        message: 'Error updating details',
        type: 'ERROR',
      });
    }
  });

  if (isLoading && !data) {
    return <LoaderOverlay />;
  }

  return (
    <Box paddingBottom='16px' textAlign={'right'} w={'100%'}>
      <Text variant='bodyLargestRegular'>
        <FormattedMessage id='profile.heading' defaultMessage={'Your profile'} />
      </Text>
      <Text mt={'12px'} textStyle='bodySmallRegular'>
        {data?.phone}
      </Text>
      <Box bg='white' m='40px auto' padding={'16px'} rounded={'16px'}>
        <Text textStyle='subtitleLarge'>
          <FormattedMessage
            id='profile.personalInfoHeader'
            defaultMessage={'Personal Information'}
          />
        </Text>
        <SimpleGrid as={'form'} onSubmit={onSubmit} padding={'16px 0'} gap={'16px'}>
          <Controller
            defaultValue=''
            control={control}
            name='name'
            render={({ field, fieldState: { error } }) => (
              <Input
                errorMsg={error?.message}
                label={<FormattedMessage id='profile.fullName' defaultMessage={'الاسم الكامل'} />}
                id='FullNameInput'
                borderRadius={12}
                color='dark.D900'
                focusBorderColor={error ? 'red' : 'green.G500'}
                aria-invalid={Boolean(errors.name)}
                aria-errormessage={!errors.name ? undefined : 'nameError'}
                {...field}
              />
            )}
          />
          <Controller
            defaultValue='user@example.com'
            control={control}
            name='email'
            render={({ field, fieldState: { error } }) => (
              <Input
                errorMsg={error?.message}
                label={<FormattedMessage id='profile.email' defaultMessage={'Email'} />}
                id='emailInput'
                data-testid='emailInput'
                borderRadius={12}
                color='dark.D900'
                focusBorderColor={error ? 'red' : 'green.G500'}
                aria-invalid={Boolean(errors.email)}
                aria-errormessage={!errors.email ? undefined : 'emailError'}
                optional
                {...field}
                value={!field.value ? '' : field.value}
                placeholder='example@example.com'
              />
            )}
          />
          <Button
            isLoading={isSubmitting}
            type='submit'
            rounded={'16px'}
            variant='primaryLarge'
            isDisabled={!isDirty}
          >
            <FormattedMessage id='profile.save' />
          </Button>
        </SimpleGrid>
      </Box>
    </Box>
  );
};
